import { useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ProfilePromptCard } from "./ProfilePromptCard";
import { useSavedPrompts } from "@/hooks/prompts/use-saved-prompts";

interface SavedPromptsGridProps {
  userId: string;
}

export function SavedPromptsGrid({ userId }: SavedPromptsGridProps) {
  const navigate = useNavigate();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  const {
    prompts,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    removePrompt,
    totalCount,
  } = useSavedPrompts(userId);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMore]);

  // Handle unsave - remove from grid immediately
  const handleUnsave = useCallback((promptId: string) => {
    removePrompt(promptId);
  }, [removePrompt]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="text-sm text-muted-foreground">Loading saved prompts...</div>
        <div className="grid gap-4 sm:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (prompts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Bookmark className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No saved prompts yet</h3>
        <p className="text-muted-foreground max-w-sm mb-6">
          Browse the feed and bookmark prompts you want to revisit later!
        </p>
        <Button onClick={() => navigate("/prompts")}>
          Browse Prompts
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Prompt count */}
      <div className="text-sm text-muted-foreground">
        {totalCount} saved {totalCount === 1 ? 'prompt' : 'prompts'}
      </div>

      {/* Prompts grid */}
      <div className="grid gap-4 sm:grid-cols-2">
        {prompts.map((prompt) => (
          <ProfilePromptCard
            key={prompt.id}
            prompt={prompt}
            onUnsave={() => handleUnsave(prompt.id)}
          />
        ))}
      </div>

      {/* Load more trigger */}
      <div ref={loadMoreRef} className="h-10 flex items-center justify-center">
        {loadingMore && (
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        )}
      </div>
    </div>
  );
}
